import { useState } from 'react';
import { ApiError, api, type ConnectorInfo } from '../api/client';

/**
 * "Connect a site" — the onboarding panel (ADR-0010 / ADR-0014).
 *
 * Mints a short-lived, single-use connect token on open and walks the operator through enrolling a
 * WordPress site: install the two plugins, paste the platform URL + token into Guard Dog → Cloud.
 * Rendered only for mutator roles (the mint endpoint is owner/admin).
 */
export function ConnectSite({ canMintToken }: { canMintToken: boolean }) {
  const [open, setOpen] = useState(false);
  const [minted, setMinted] = useState<{ token: string; expiresAt: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState<'url' | 'token' | null>(null);
  const [info, setInfo] = useState<ConnectorInfo | null | undefined>(undefined);

  // The console is served same-origin from the API Worker (ADR-0013), so this origin IS the
  // platform URL the plugin needs — no more guessing/typo-ing the API host.
  const platformUrl = window.location.origin;

  async function mint() {
    setBusy(true);
    setError(null);
    try {
      setMinted(await api.connectToken());
    } catch (e) {
      setMinted(null);
      // Prefer the API's own reason ("site limit reached") over a bare status code — it is the
      // difference between an answer and a mystery.
      setError(
        e instanceof ApiError
          ? e.explained
            ? `Could not mint a token — ${e.message}.`
            : `Could not mint a token (${e.status || 'network'})`
          : 'Could not mint a token',
      );
    } finally {
      setBusy(false);
    }
  }

  function toggle() {
    if (!canMintToken) return;
    const opening = !open;
    setOpen(opening);
    if (opening && !minted) void mint();
    if (opening && info === undefined) {
      // Latest published release, for the version label; null (no release / error) hides it.
      api.connectorInfo().then(setInfo, () => setInfo(null));
    }
  }

  async function copy(kind: 'url' | 'token', value: string) {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(kind);
      window.setTimeout(() => setCopied((c) => (c === kind ? null : c)), 1500);
    } catch {
      // Clipboard unavailable (permissions/http) — the value is selectable text either way.
    }
  }

  const expiresLabel = minted
    ? new Date(minted.expiresAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    : null;

  return (
    <div className="connect-site">
      <button
        type="button"
        className="btn-primary"
        onClick={toggle}
        aria-expanded={open}
        disabled={!canMintToken}
        aria-describedby={!canMintToken ? 'connect-site-restricted' : undefined}
      >
        {open ? 'Close' : 'Connect a site'}
      </button>
      {!canMintToken && (
        <p id="connect-site-restricted" className="restriction-note">
          New site enrollment is paused until the plan is active. Existing sites keep protecting
          traffic.{' '}
          <a href="https://guarddog.cc/" target="_blank" rel="noreferrer">
            Contact us to upgrade
          </a>
          .
        </p>
      )}

      {open && (
        <div className="connect-panel">
          <ol className="connect-steps">
            <li>
              Install the <strong>Guard Dog</strong> plugin, then upload the{' '}
              <a href="/v1/console/connector/download" download>
                Guard&nbsp;Dog&nbsp;Connector
              </a>
              {info?.latestVersion ? ` (v${info.latestVersion})` : ''} (Plugins → Add New → Upload)
              and activate both.
            </li>
            <li>
              In wp-admin, open <strong>Guard Dog → Cloud</strong> and enter:
            </li>
          </ol>

          <div className="token-row">
            <span className="label">Platform URL</span>
            <code className="mono">{platformUrl}</code>
            <button
              type="button"
              className="copy-btn"
              onClick={() => void copy('url', platformUrl)}
            >
              {copied === 'url' ? 'Copied ✓' : 'Copy'}
            </button>
          </div>

          <div className="token-row">
            <span className="label">Connect token</span>
            {minted ? (
              <>
                <code className="mono token-value">{minted.token}</code>
                <button
                  type="button"
                  className="copy-btn"
                  onClick={() => void copy('token', minted.token)}
                >
                  {copied === 'token' ? 'Copied ✓' : 'Copy'}
                </button>
              </>
            ) : (
              <span className="muted">{busy ? 'minting…' : '—'}</span>
            )}
          </div>

          {error && <p className="error">{error}</p>}

          <p className="muted">
            {expiresLabel
              ? `Valid until ${expiresLabel} (≈15 minutes) or until this token enrolls one site. `
              : ''}
            <button
              type="button"
              className="link-button"
              onClick={() => void mint()}
              disabled={busy}
            >
              Mint a new token
            </button>
          </p>
        </div>
      )}
    </div>
  );
}
